import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { commentsService } from '../../services';
import { translate_error } from '../../utils/translate_error';

/**
 * @file commentsSlice.js
 * @description Redux slice for managing artwork comments and replies.
 *
 * NAMING CONVENTION:
 * - Slice name: "comments" (aligned with DB table + service)
 * - State: state.comments.comments_by_artwork, loading_comments, error
 * - Thunks: fetchArtworkComments, addCommentAsync, addCommentReplyAsync, deleteCommentAsync
 * - Reducers: clearCommentsError, clearArtworkComments
 *
 * ARCHITECTURE:
 * - Supabase direct integration (no Backend Custom API)
 * - Comments stored in object keyed by artwork_id for efficient lookup
 * - Async thunks for DB sync
 *
 * STATE STRUCTURE:
 * {
 *   comments_by_artwork: {
 *     'artwork-uuid-1': [ { id, content, user, comment_replies, ... }, ... ],
 *     'artwork-uuid-2': [ ... ]
 *   },
 *   loading_comments: false,
 *   error: null
 * }
 */

// ============================================
// ASYNC THUNKS
// ============================================

/**
 * Fetch comments for a specific artwork from Supabase
 * @param {string} artwork_id - Artwork UUID
 * @returns {Object} { artwork_id, comments } - Comments array with user and replies data
 */
export const fetchArtworkComments = createAsyncThunk(
  'comments/fetchArtworkComments',
  async (artwork_id, { rejectWithValue }) => {
    try {
      const { data, error } = await commentsService.getArtworkComments(artwork_id);
      if (error) throw new Error(error);
      return { artwork_id, comments: data || [] };
    } catch (error) {
      return rejectWithValue(translate_error(error.message));
    }
  }
);

/**
 * Add a new comment to an artwork (async - persists to DB)
 * @param {Object} params
 * @param {string} params.artwork_id - Artwork UUID
 * @param {string} params.user_id - User UUID
 * @param {string} params.content - Comment text content
 * @returns {Object} { artwork_id, comment } - Newly created comment with user data
 */
export const addCommentAsync = createAsyncThunk(
  'comments/addCommentAsync',
  async ({ artwork_id, user_id, content }, { rejectWithValue }) => {
    try {
      const { data, error } = await commentsService.addComment(artwork_id, user_id, content);
      if (error) throw new Error(error);

      return { artwork_id, comment: data };
    } catch (error) {
      return rejectWithValue(translate_error(error.message));
    }
  }
);

/**
 * Add a reply to a comment (async - persists to DB)
 * @param {Object} params
 * @param {string} params.artwork_id - Artwork UUID (for state update)
 * @param {string} params.comment_id - Parent comment UUID
 * @param {string} params.user_id - User UUID
 * @param {string} params.content - Reply text content
 * @returns {Object} { artwork_id, comment_id, reply } - Newly created reply with user data
 */
export const addCommentReplyAsync = createAsyncThunk(
  'comments/addCommentReplyAsync',
  async ({ artwork_id, comment_id, user_id, content }, { rejectWithValue }) => {
    try {
      const { data, error } = await commentsService.addCommentReply(comment_id, user_id, content);
      if (error) throw new Error(error);

      return { artwork_id, comment_id, reply: data };
    } catch (error) {
      return rejectWithValue(translate_error(error.message));
    }
  }
);

/**
 * Delete a comment (async - persists to DB)
 * @param {Object} params
 * @param {string} params.artwork_id - Artwork UUID (for state update)
 * @param {string} params.comment_id - Comment UUID to delete
 * @param {string} params.user_id - User UUID (for authorization)
 * @returns {Object} { artwork_id, comment_id } - Deleted comment identifiers
 */
export const deleteCommentAsync = createAsyncThunk(
  'comments/deleteCommentAsync',
  async ({ artwork_id, comment_id, user_id }, { rejectWithValue }) => {
    try {
      const { error } = await commentsService.deleteComment(comment_id, user_id);
      if (error) throw new Error(error);

      return { artwork_id, comment_id };
    } catch (error) {
      return rejectWithValue(translate_error(error.message));
    }
  }
);

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
  comments_by_artwork: {},  // Object keyed by artwork_id containing arrays of comments
  loading_comments: false,  // Loading state for async operations
  error: null               // Error message (string or null)
};

// ============================================
// SLICE DEFINITION
// ============================================

export const commentsSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    /**
     * Clear error state
     */
    clearCommentsError: (state) => {
      state.error = null;
    },

    /**
     * Clear comments for a specific artwork
     * Used when navigating away from artwork detail page
     * @param {Object} action.payload - { artwork_id }
     */
    clearArtworkComments: (state, action) => {
      const { artwork_id } = action.payload;
      delete state.comments_by_artwork[artwork_id];
    },

    /**
     * Clear all comments (used on logout or session reset)
     */
    clearAllComments: (state) => {
      state.comments_by_artwork = {};
      state.loading_comments = false;
      state.error = null;
    }
  },

  // ============================================
  // EXTRA REDUCERS (Async Thunks)
  // ============================================

  extraReducers: (builder) => {
    builder
      // -------------------- fetchArtworkComments --------------------
      .addCase(fetchArtworkComments.pending, (state) => {
        state.loading_comments = true;
        state.error = null;
      })
      .addCase(fetchArtworkComments.fulfilled, (state, action) => {
        state.loading_comments = false;
        const { artwork_id, comments } = action.payload;
        state.comments_by_artwork[artwork_id] = comments;
      })
      .addCase(fetchArtworkComments.rejected, (state, action) => {
        state.loading_comments = false;
        state.error = action.payload;
      })

      // -------------------- addCommentAsync --------------------
      .addCase(addCommentAsync.pending, (state) => {
        state.loading_comments = true;
        state.error = null;
      })
      .addCase(addCommentAsync.fulfilled, (state, action) => {
        state.loading_comments = false;
        const { artwork_id, comment } = action.payload;

        // Initialize array if it doesn't exist
        if (!state.comments_by_artwork[artwork_id]) {
          state.comments_by_artwork[artwork_id] = [];
        }

        // Add new comment to the beginning (newest first)
        state.comments_by_artwork[artwork_id].unshift(comment);
      })
      .addCase(addCommentAsync.rejected, (state, action) => {
        state.loading_comments = false;
        state.error = action.payload;
      })

      // -------------------- addCommentReplyAsync --------------------
      .addCase(addCommentReplyAsync.pending, (state) => {
        state.loading_comments = true;
        state.error = null;
      })
      .addCase(addCommentReplyAsync.fulfilled, (state, action) => {
        state.loading_comments = false;
        const { artwork_id, comment_id, reply } = action.payload;

        // Find the parent comment and add the reply
        const comments = state.comments_by_artwork[artwork_id] || [];
        const parent_comment = comments.find(c => c.id === comment_id);

        if (parent_comment) {
          // Initialize comment_replies array if it doesn't exist
          if (!parent_comment.comment_replies) {
            parent_comment.comment_replies = [];
          }
          // Add new reply to the end
          parent_comment.comment_replies.push(reply);
        }
      })
      .addCase(addCommentReplyAsync.rejected, (state, action) => {
        state.loading_comments = false;
        state.error = action.payload;
      })

      // -------------------- deleteCommentAsync --------------------
      .addCase(deleteCommentAsync.pending, (state) => {
        state.loading_comments = true;
        state.error = null;
      })
      .addCase(deleteCommentAsync.fulfilled, (state, action) => {
        state.loading_comments = false;
        const { artwork_id, comment_id } = action.payload;

        // Remove the comment from the array
        const comments = state.comments_by_artwork[artwork_id] || [];
        state.comments_by_artwork[artwork_id] = comments.filter(c => c.id !== comment_id);
      })
      .addCase(deleteCommentAsync.rejected, (state, action) => {
        state.loading_comments = false;
        state.error = action.payload;
      });
  }
});

// ============================================
// EXPORTS
// ============================================

export const {
  clearCommentsError,
  clearArtworkComments,
  clearAllComments
} = commentsSlice.actions;

export default commentsSlice.reducer;
